/**
 * Utility functions for handling image URLs and serving
 */

/**
 * Get the correct URL for serving an uploaded image
 * @param {string} imagePath - The image path (could be relative or absolute)
 * @returns {string} - The complete URL to serve the image
 */
export const getImageUrl = (imagePath) => {
  if (!imagePath) {
    return null;
  }

  // If it's already a complete URL, return as is
  if (imagePath.startsWith('http')) {
    return imagePath;
  }

  // Get backend URL from environment
  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
  
  // If path starts with /uploads/, remove it for the serve endpoint
  let servePath = imagePath;
  if (imagePath.startsWith('/uploads/')) {
    servePath = imagePath.replace('/uploads/', '');
  } else if (imagePath.startsWith('uploads/')) {
    servePath = imagePath.replace('uploads/', '');
  }
  
  // Return the complete URL through the serve endpoint
  return `${backendUrl}/api/upload/serve/${servePath}`;
};

/**
 * Get robust image URL with intelligent fallbacks
 * @param {string} imagePath - The image path
 * @param {string} category - Category for default images (ingredients, products, etc.)
 * @param {string} itemId - ID of the item for specific default image
 * @returns {string} - The robust image URL
 */
export const getImageUrlWithFallback = (imagePath, category = 'products', itemId = null, useCache = true) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
  
  // If we have a valid image path, try to use it
  if (imagePath) {
    let imageUrl = getImageUrl(imagePath);
    if (imageUrl) {
      // Add cache control parameter based on environment and useCache flag
      if (!useCache && process.env.NODE_ENV === 'development') {
        const separator = imageUrl.includes('?') ? '&' : '?';
        imageUrl += `${separator}nocache=${Date.now()}`;
      }
      return imageUrl;
    }
  }
  
  // Use robust image endpoint for reliable serving
  if (itemId && category) {
    let robustUrl = `${backendUrl}/api/upload/robust/${category}/${itemId}`;
    // Add cache control parameter for robust URLs too
    if (!useCache && process.env.NODE_ENV === 'development') {
      const separator = robustUrl.includes('?') ? '&' : '?';
      robustUrl += `${separator}nocache=${Date.now()}`;
    }
    return robustUrl;
  }
  
  // Fall back to category default
  let defaultUrl = `${backendUrl}/api/upload/robust/${category}/default`;
  // Add cache control parameter for default URLs too
  if (!useCache && process.env.NODE_ENV === 'development') {
    const separator = defaultUrl.includes('?') ? '&' : '?';
    defaultUrl += `${separator}nocache=${Date.now()}`;
  }
  return defaultUrl;
};

/**
 * Get robust image URL directly (new preferred method)
 * @param {string} category - Category (products, ingredients, bottles)
 * @param {string} itemId - Item identifier
 * @param {string} originalPath - Original image path (optional)
 * @returns {string} - The robust image URL
 */
export const getRobustImageUrl = (category = 'products', itemId = 'default', originalPath = null) => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
  
  // If we have an original path, try the regular serve endpoint first
  if (originalPath) {
    return getImageUrl(originalPath);
  }
  
  // Use robust endpoint
  return `${backendUrl}/api/upload/robust/${category}/${itemId}`;
};

/**
 * Generate multiple fallback URLs for maximum reliability
 * @param {string} imagePath - Original image path
 * @param {string} category - Category
 * @param {string} itemId - Item ID
 * @returns {Array} - Array of fallback URLs in order of preference
 */
export const generateImageFallbacks = (imagePath, category = 'products', itemId = 'default') => {
  const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
  const urls = [];
  
  // 1. Original image if provided
  if (imagePath) {
    urls.push(getImageUrl(imagePath));
  }
  
  // 2. Robust specific item
  if (itemId && itemId !== 'default') {
    urls.push(`${backendUrl}/api/upload/robust/${category}/${itemId}`);
  }
  
  // 3. Robust category default
  urls.push(`${backendUrl}/api/upload/robust/${category}/default`);
  
  // 4. Robust global default
  urls.push(`${backendUrl}/api/upload/robust/products/default`);
  
  return urls.filter(url => url !== null);
};

/**
 * Check if an image path represents an uploaded file
 * @param {string} imagePath - The image path
 * @returns {boolean} - True if it's an uploaded file
 */
export const isUploadedImage = (imagePath) => {
  if (!imagePath) return false;
  return imagePath.includes('/uploads/') || imagePath.includes('uploads/');
};